import React, { useEffect, useRef, useState } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import empty from '../assets/images/empty-filter.svg';
import { Link, useNavigate } from 'react-router-dom';
import Config from '../API/Config';
import { FaDollarSign, FaEdit, FaFireAlt, FaHome, FaRupeeSign, FaTag, FaTrash } from 'react-icons/fa';
import { getCartProduct, deleteCartProduct, UpdateCart } from '../API/ProductAPI';
import { ApplyCoupon, createOrder } from '../API/OrderAPI';
import { AddAddress, DeleteAddress, getAddressById, UpdateAddress } from '../API/AddressAPI';
import { toast } from 'sonner';
import { LuTrash2 } from 'react-icons/lu';
import { useCart } from '../context/CartContext';
import { PuffLoader } from 'react-spinners';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { FaLocationCrosshairs } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';
import Swal from 'sweetalert2';

const Cart = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartTotal, setCartTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [charges, setCharges] = useState(null);
  const [tax, setTax] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const { refreshCartCount } = useCart();

  // Address state
  const [openModal, setOpenModal] = useState(false);
  const modalRef = useRef(null);
  const [addresses, setAddresses] = useState([]);
  const [editAddress, setEditAddress] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addButtonLoading, setAddButtonLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [disabledFields, setDisabledFields] = useState({
    AddressLine1: false, City: false, State: false, Country: false, Pincode: false,
  });

  const navigate = useNavigate();
  const StateId = Config.getStateId();
  const CountryId = Config.getCountryId();
  const currencySymbol = CountryId === 'IN' ? <FaRupeeSign className="inline" /> : <FaDollarSign className="inline" />;

  const fetchCartProducts = async (stateId = StateId, countryId = CountryId) => {
    setLoading(true);
    try {
      const res = await getCartProduct(countryId, stateId);
      if (res?.status && res?.responseData) {
        const { cartItems, deliveryandservicecharge, cartTotal, grandTotal, tax } = res.responseData;
        setProducts(cartItems || []);
        setCharges(deliveryandservicecharge || null);
        setCartTotal(cartTotal || 0);
        setGrandTotal(grandTotal || 0);
        setTax(tax || null);
      } else {
        setProducts([]);
        setCharges(null);
        setCartTotal(0);
        setGrandTotal(0);
        setTax(null);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load cart items.");
    } finally {
      setLoading(false);
    }
  };

  // Address logic
  const fetchAddress = async (preferredId = null) => {
    setAddressLoading(true);
    try {
      const res = await getAddressById();
      if (res && res.length > 0) {
        setAddresses(res);
        const addr = res.find(a => a.Id === preferredId) || res.find(a => a.Id === selectedAddressId) || res[0];
        setSelectedAddressId(addr.Id);
        fetchCartProducts(addr.State, addr.Country);
      } else {
        setAddresses([]);
        fetchCartProducts();
      }
    } catch (err) {
      console.error('Error fetching address', err);
      fetchCartProducts();
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => { fetchAddress(); }, []);

  useEffect(() => {
    if (openModal) {
      setDisabledFields(editAddress ? {
        AddressLine1: !!editAddress.AddressLine1,
        City: !!editAddress.City,
        State: !!editAddress.State,
        Country: !!editAddress.Country,
        Pincode: !!editAddress.Pincode,
      } : { AddressLine1: false, City: false, State: false, Country: false, Pincode: false });
    }
  }, [openModal, editAddress]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) setOpenModal(false);
    };
    if (openModal) document.addEventListener('mousedown', handleClickOutside);
    else document.removeEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openModal]);

  const parseAddressComponents = (result) => {
    const components = result.address_components;
    const geometry = result.geometry;
    let name = '', area = '', city = '', state = '', country = '', pincode = '';
    components.forEach((comp) => {
      if (comp.types.includes('premise') || comp.types.includes('street_number')) name = comp.long_name;
      else if (comp.types.includes('route')) area = comp.long_name;
      else if (comp.types.includes('sublocality') || comp.types.includes('sublocality_level_1'))
        area = area ? `${area}, ${comp.long_name}` : comp.long_name;
      else if (comp.types.includes('locality')) city = comp.long_name;
      else if (comp.types.includes('administrative_area_level_1')) state = comp.long_name;
      else if (comp.types.includes('country'))
        country = comp.short_name === 'IN' ? 'IN' : comp.short_name === 'US' ? 'US' : 'IN';
      else if (comp.types.includes('postal_code')) pincode = comp.long_name;
    });
    const lat = typeof geometry.location.lat === 'function' ? geometry.location.lat() : geometry.location.lat;
    const lng = typeof geometry.location.lng === 'function' ? geometry.location.lng() : geometry.location.lng;
    return { name, address: result.formatted_address, area, city, state, country, lat, lng, pincode };
  };

  const handlePlaceSelect = async (place) => {
    try {
      const results = await geocodeByPlaceId(place.value.place_id);
      if (results && results.length > 0) {
        const parsed = parseAddressComponents(results[0]);
        let street = [parsed.name, parsed.area].filter(Boolean).join(', ');
        if (!street) street = parsed.address.split(',')[0];
        formik.setValues({ ...formik.values, AddressLine1: street, City: parsed.city, Area: parsed.area, State: parsed.state, Country: parsed.country, Pincode: parsed.pincode, Lat: parsed.lat.toString(), Lng: parsed.lng.toString() });
        setDisabledFields({ AddressLine1: !!street, City: !!parsed.city, State: !!parsed.state, Country: !!parsed.country, Pincode: !!parsed.pincode });
      }
    } catch (error) { console.error('Error selecting place:', error); }
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude: lat, longitude: lng } = position.coords;
      try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4`);
        const data = await response.json();
        if (data.status === 'OK' && data.results.length > 0) {
          const parsed = parseAddressComponents(data.results[0]);
          let street = [parsed.name, parsed.area].filter(Boolean).join(', ');
          if (!street) street = parsed.address.split(',')[0];
          formik.setValues({ ...formik.values, AddressLine1: street, City: parsed.city, Area: parsed.area, State: parsed.state, Country: parsed.country, Pincode: parsed.pincode, Lat: lat.toString(), Lng: lng.toString() });
          setDisabledFields({ AddressLine1: !!street, City: !!parsed.city, State: !!parsed.state, Country: !!parsed.country, Pincode: !!parsed.pincode });
        }
      } catch (error) { console.error('Error fetching address from coords:', error); }
      finally { setLocationLoading(false); }
    }, (error) => { console.error('Geolocation error:', error); setLocationLoading(false); });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      AddressLine1: editAddress?.AddressLine1 || '',
      LandMark: editAddress?.LandMark || '',
      City: editAddress?.City || '',
      State: editAddress?.State || '',
      Country: editAddress?.Country || 'IN',
      Pincode: editAddress?.Pincode || '',
      Lat: editAddress?.Lat || '',
      Lng: editAddress?.Lng || '',
    },
    validationSchema: Yup.object({
      AddressLine1: Yup.string().required('Address is required'),
      LandMark: Yup.string().required('Landmark is required'),
      City: Yup.string().required('City is required'),
      State: Yup.string().required('State is required'),
      Country: Yup.string().required('Country is required'),
      Pincode: Yup.string().required('Zip code is required').matches(/^\d{5,6}$/, 'Invalid zip code format'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setFormLoading(true);
      try {
        const payload = { ...values, Lat: values.Lat || '0.0000', Lng: values.Lng || '0.0000' };
        const res = editAddress
          ? await UpdateAddress({ Id: editAddress.Id, ...payload })
          : await AddAddress(payload);
        if (res.status === true) {
          const newId = !editAddress && res.responseData?.Id ? res.responseData.Id : (editAddress?.Id || null);
          setOpenModal(false);
          setEditAddress(null);
          fetchAddress(newId);
          resetForm();
        }
      } catch (err) { console.error('Submit Error', err); }
      finally { setFormLoading(false); }
    },
  });

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Address?',
      text: 'Are you sure you want to delete this address? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });
    if (result.isConfirmed) {
      try {
        const res = await DeleteAddress({ Id: id });
        if (res?.status) {
          toast.success('Address has been deleted.');
          const nextAddr = addresses.find(a => a.Id !== id);
          fetchAddress(nextAddr?.Id || null);
        }
      } catch (err) { console.error('Delete error', err); toast.error('Something went wrong.'); }
    }
  };

  const updateQuantity = async (id, newQty) => {
    if (newQty < 1) return;
    try {
      const payload = {
        Id: parseInt(id),
        Qty: Math.abs(newQty),
        CountryId,
        StateId,
      };
      const token = JSON.parse(sessionStorage.getItem("AuthToken"));
      const response = await UpdateCart(payload, token);
      if (response.status) {
        toast.success(response.message || `Quantity updated successfully!`);
        setAppliedCoupon(null);
        setPromoCode('');
        await fetchCartProducts();
        refreshCartCount();
      } else {
        toast.error(response.message || "Failed to update quantity.");
      }
    } catch (error) {
      console.error("Error updating quantity:", error);
      toast.error("An error occurred while updating quantity.");
    }
  };

  const removeItem = async (id) => {
    try {
      await deleteCartProduct(id, CountryId, StateId);
      toast.success("Item removed from cart successfully!");
      setAppliedCoupon(null);
      setPromoCode('');
      await fetchCartProducts();
      refreshCartCount();
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("An error occurred while removing item.");
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a valid promo code.");
      return;
    }
    setCouponLoading(true);
    try {
      const res = await ApplyCoupon({ CODE: promoCode, CountryId, StateId });
      if (res?.status) {
        setAppliedCoupon(res.responseData);
        toast.success(res.message || "Coupon applied successfully!");
      } else {
        toast.error(res?.message || "Invalid promo code.");
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error("Failed to apply coupon.");
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setPromoCode('');
    setGrandTotal(grandTotal);
    toast.success("Coupon removed.");
  };

  const generateTransactionId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const length = Math.floor(Math.random() * 3) + 10;
    return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const handleOrderNow = async () => {
    if (!selectedAddressId) {
      toast.error('Please select a delivery address to proceed.');
      return;
    }
    setFormLoading(true);
    try {
      const res = await createOrder({
        AddressId: selectedAddressId.toString(),
        CouponCode: promoCode || '',
        TransactionId: generateTransactionId(),
      });
      if (res.status === true) {
        navigate(`/order-success/${res.responseData.OrderId}`);
      } else {
        navigate('/order-fail');
      }
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const discountAmount = appliedCoupon ? appliedCoupon.discount : 0;
  const totalToPay = appliedCoupon ? appliedCoupon.grandTotal : grandTotal;

  if (loading) {
    return (
      <div className="bg-gray-50">
        <Breadcrumb title="Cart" path="Cart" />
        <div className="container mx-auto px-4 py-8 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left skeleton: items + address */}
            <div className="lg:col-span-2 space-y-4">
              {[1].map((i) => (
                <div key={i} className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-gray-100 animate-pulse">
                  <div className="flex items-center space-x-4 w-full">
                    <div className="w-20 h-20 bg-gray-200 rounded-md flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                      <div className="h-3 bg-gray-200 rounded w-1/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-3 ml-4">
                    <div className="h-5 bg-gray-200 rounded w-16" />
                    <div className="h-8 bg-gray-200 rounded w-28" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
              {/* Address skeleton */}
              <div className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-3" />
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-6" />
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2].map((i) => (
                    <div key={i} className="border rounded-lg p-4 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-1/3" />
                      <div className="h-4 bg-gray-200 rounded w-full" />
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-8 bg-gray-200 rounded w-1/3 mt-2" />
                    </div>
                  ))}
                </div>
                <div className="h-9 bg-gray-200 rounded w-36 mt-4" />
              </div>
            </div>
            {/* Summary skeleton */}
            <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-sm animate-pulse space-y-4">
              <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto" />
              <div className="h-16 bg-gray-200 rounded w-full" />
              <div className="h-10 bg-gray-200 rounded w-full" />
              <div className="space-y-3 pt-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                  </div>
                ))}
              </div>
              <div className="h-12 bg-gray-200 rounded-full w-full mt-4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {couponLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}
      <Breadcrumb title="Cart" path="Cart" />
      <div className="container mx-auto px-4 py-8 md:py-16">
        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <img src={empty} className="w-32 md:w-48 mx-auto" alt="Empty Cart" />
            <h2 className="mt-6 text-2xl md:text-3xl font-bold text-darktext text-center">
              Your Cart is Empty
            </h2>
            <p className="mt-2 text-sm md:text-base text-darktext text-center">
              Explore our products and add items to your cart!
            </p>
            <Link
              to="/menu"
              className="mt-6 inline-block bg-theme2 text-white px-6 py-3 rounded-full font-semibold text-sm md:text-base hover:bg-black transition-colors duration-300"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Items + Address */}
            <div className="lg:col-span-2 space-y-4">
              {products.map((product) => {
                const price = parseFloat(product.SalePrice || product.RegularPrice || 0);
                const lineTotal = price * (product.Qty || 0);
                const originalPrice = parseFloat(product.RegularPrice || 0);
                const calories = parseInt(product.Calories || 0);
                return (
                  <div key={product.Id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100">
                    <div className="flex items-center space-x-4 w-full sm:w-auto">
                      <img src={product.ProductThumbImage} alt={product.ProductName} className="w-20 h-20 object-cover rounded-md flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-darktext truncate">{product.ProductName}</h3>
                        <p className="text-sm text-darktext mt-1">{product.VariationName} {product.PackSize ? `(Pack of ${product.PackSize})` : ''}</p>
                        {/* <p className="text-xs text-gray-400 mt-0.5">SKU: {product.SKU}</p> */}
                        {calories > 0 && <p className="text-xs text-contentcolor mt-1 flex items-center"><FaFireAlt className="mr-1" /> {calories} cal/serving</p>}
                        <div className="mt-1 flex items-center gap-2">
                          {originalPrice > price && <span className="text-xs text-gray-400 line-through">{currencySymbol} {originalPrice.toFixed(2)}</span>}
                          <span className="text-sm font-semibold text-green-600">{currencySymbol} {price.toFixed(2)}</span>
                          {/* {product.Tax_Vat > 0 && <span className="text-xs text-gray-400">+{product.Tax_Vat}% tax</span>} */}
                        </div>
                        {!product.isAvailable && <span className="text-xs text-red-500 font-medium mt-1 block">Out of Stock</span>}
                      </div>
                    </div>
                    <div className="mt-4 sm:mt-0 flex md:flex-col md:items-end items-center w-full sm:w-auto space-y-3">
                      <p className="text-base md:text-lg flex justify-center items-center font-semibold text-darktext">{currencySymbol} {lineTotal.toFixed(2)}</p>
                      <div className="flex items-center justify-center w-full sm:w-auto">
                        <button onClick={() => updateQuantity(product.Id, product.Qty - 1)} className="bg-gray-100 text-theme2 px-3.5 py-1.5 rounded-tl-md rounded-bl-md hover:bg-theme2 border border-[#83442b8c] hover:text-white focus:outline-none transition-colors duration-200 min-w-[30px]" disabled={product.Qty <= 1}>-</button>
                        <span className="w-12 text-center font-semibold text-darktext px-1.5 py-1.5 border-t border-b border-[#83442b8c]">{product.Qty}</span>
                        <button onClick={() => updateQuantity(product.Id, product.Qty + 1)} className="bg-gray-100 text-theme2 px-3.5 py-1.5 rounded-tr-md rounded-br-md hover:bg-theme2 border border-[#83442b8c] hover:text-white focus:outline-none transition-colors duration-200 min-w-[30px]">+</button>
                      </div>
                      <button onClick={() => removeItem(product.Id)} className="text-red-500 hover:text-red-600 transition-colors duration-200 flex items-center text-sm font-medium">
                        <LuTrash2 className="text-xl mr-1" /> Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Address Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl md:text-2xl font-bold text-darktext mb-2">Select Delivery Address</h2>
                <p className="text-contentcolor mb-6 text-sm">You've added some addresses before. You can select one below.</p>
                {addressLoading ? (
                  <div className="flex justify-center items-center h-32"><PuffLoader color={'#83442B'} size={60} /></div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {addresses.length > 0 ? addresses.map((addr) => (
                      <div key={addr.Id} onClick={() => { setSelectedAddressId(addr.Id); setAppliedCoupon(null); setPromoCode(''); fetchCartProducts(addr.State, addr.Country); }} className={`border rounded-lg relative bg-white cursor-pointer transition-colors duration-200 ${selectedAddressId === addr.Id ? 'border-theme2' : 'hover:border-gray-300'}`}>
                        <div className="flex items-center gap-2 p-3.5 py-4 border-b text-lg font-semibold mb-2">
                          <FaHome className="text-theme2" /> {addr.LandMark || 'Home'}
                          <input
                            type="radio"
                            readOnly
                            checked={selectedAddressId === addr.Id}
                            className="ml-auto accent-[#83442B] w-4 h-4 cursor-pointer"
                          />
                        </div>
                        <div className="p-4">
                          <p className="text-darktext text-base">{addr.AddressLine1}, {addr.City}, {addr.State}, {addr.Country}-{addr.Pincode}</p>
                        </div>
                        <div className="absolute top-4 right-10 flex gap-2">
                          <button onClick={(e) => { e.stopPropagation(); setEditAddress(addr); setOpenModal(true); }} className="text-sm px-3 py-1 rounded-full bg-[#e1b37d1f] text-theme2 flex items-center gap-1">
                            <FaEdit className="text-xs" /> Edit
                          </button>
                          <button className="text-sm text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(addr.Id); }}><FaTrash /></button>
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-2 text-center py-8">
                        <p className="text-gray-500 text-lg">No addresses found. Please add your first address.</p>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { setEditAddress(null); formik.resetForm(); setOpenModal(true); setAddButtonLoading(true); setTimeout(() => setAddButtonLoading(false), 500); }}
                  className="bg-theme2 mt-4 w-full md:w-auto hover:bg-black text-white px-6 py-2 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={addButtonLoading}
                >
                  {addButtonLoading ? 'Adding...' : 'Add New Address'}
                </button>
              </div>
            </div>{/* end left col-span-2 */}

            {/* Cart Summary */}
            <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-sm sticky top-6 self-start">
              <h2 className="text-xl md:text-2xl font-bold text-darktext mb-5 text-center">
                Cart Summary
              </h2>

              {/* Selected Address */}
              {(() => {
                const selectedAddress = addresses.find(addr => addr.Id === selectedAddressId);
                return selectedAddress ? (
                  <div className="border rounded-md p-3 flex items-start mb-5">
                    <img src="https://cdn-icons-png.flaticon.com/512/854/854878.png" alt="map" className="w-12 h-12 mr-3" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-darktext text-sm">
                        Deliver to: <span className="font-normal">{selectedAddress.LandMark || 'Home'}</span>
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {selectedAddress.AddressLine1}, {selectedAddress.City}, {selectedAddress.State}, {selectedAddress.Country}-{selectedAddress.Pincode}
                      </p>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Coupon Section */}
              <div className="mb-5">
                <label className="text-sm font-semibold text-darktext flex items-center gap-1 mb-2">
                  <FaTag className="text-theme2" /> Promo Code
                </label>
                <div className="flex">
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="flex-1 border p-2 py-2.5 rounded-l-md focus:outline-none text-sm"
                    disabled={couponLoading || !!appliedCoupon}
                  />
                  {appliedCoupon ? (
                    <button
                      onClick={handleRemoveCoupon}
                      className="bg-red-600 text-white px-4 rounded-r-md hover:bg-red-700 transition text-sm"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={handleApplyPromo}
                      className="bg-theme2 text-white px-4 rounded-r-md hover:bg-black transition disabled:opacity-50 text-sm"
                      disabled={couponLoading}
                    >
                      APPLY
                    </button>
                  )}
                </div>
                {appliedCoupon && (
                  <p className="text-green-600 text-xs mt-1.5 flex items-center gap-1">
                    <FaTag /> Coupon <strong>{promoCode}</strong> applied!
                  </p>
                )}
              </div>

              {/* Bill Details */}
              <div className="space-y-2.5 mb-5 text-sm">

                <div className="flex justify-between text-darktext py-2 border-b border-gray-100">
                  <span>Item Total</span>
                  <span className="font-bold flex items-center gap-0.5">{currencySymbol} {cartTotal.toFixed(2)}</span>
                </div>
                {appliedCoupon && (
                  <div className="flex justify-between text-green-600 py-2 border-b border-gray-100">
                    <span>Coupon Discount ({appliedCoupon.coupon?.CODE})</span>
                    <span className="font-semibold flex items-center gap-0.5">- {currencySymbol} {discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {appliedCoupon && (
                  <div className="flex justify-between text-darktext py-2 border-b border-gray-100">
                    <span>Sub Total</span>
                    <span className="font-semibold flex items-center gap-0.5">{currencySymbol} {appliedCoupon.discountedCartTotal.toFixed(2)}</span>
                  </div>
                )}
                {tax && tax.totalTaxValue > 0 && (
                  <div className="flex justify-between text-darktext py-2 border-b border-gray-100">
                    <span>GST
                      {/* {tax.breakdown?.length > 0 && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({tax.breakdown.map(t => `${t.taxPercentage}%`).join(', ')})
                        </span>
                      )} */}
                    </span>
                    <span className="font-semibold flex items-center gap-0.5">{currencySymbol} {tax.totalTaxValue.toFixed(2)}</span>
                  </div>
                )}
                {charges && (
                  <>
                    <div className="flex justify-between text-darktext py-2 border-b border-gray-100">
                      <span>Delivery Charge</span>
                      <span className="font-semibold flex items-center gap-0.5">{currencySymbol} {Number(charges.deliveryFinalAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-darktext py-2 border-b border-gray-100">
                      {/* <span>Service Charge <span className="text-xs text-gray-400">(incl. {charges.ServiceChargeTax}% tax)</span></span> */}
                      <span>Service Charge</span>
                      <span className="font-semibold flex items-center gap-0.5">{currencySymbol} {Number(charges.serviceChargeFinalAmount).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-base font-bold text-darktext pt-3">
                  <span>Grand Total</span>
                  <span className="flex items-center gap-0.5 text-green-700">{currencySymbol} {totalToPay.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleOrderNow}
                disabled={!selectedAddressId || formLoading}
                className="block w-full bg-theme2 text-white text-center py-3 rounded-full font-semibold text-sm md:text-base hover:bg-black focus:outline-none transition-colors duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formLoading ? 'Placing Order...' : 'Place Order'}
              </button>
            </div>
          </div>
        )}
      </div>

      {openModal && (
        <div className="fixed inset-0 px-4 bg-black bg-opacity-40 flex items-center justify-center z-[70] p-4">
          {locationLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
              <PuffLoader color={'#83442B'} size={60} />
            </div>
          )}
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl max-h-[90vh] overflow-y-auto p-4 md:p-6 relative my-4" ref={modalRef}>
            {formLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[75] rounded-lg">
                <PuffLoader color={'#83442B'} size={60} />
              </div>
            )}
            <button onClick={() => { setOpenModal(false); setEditAddress(null); formik.resetForm(); }} className="absolute top-2 right-2 md:top-4 md:right-4 text-gray-500 hover:text-gray-700 z-20 p-1">
              <IoClose size={20} className="md:hidden" />
              <IoClose size={22} className="hidden md:block" />
            </button>
            <h2 className="text-lg md:text-xl font-semibold text-darktext mb-4 md:mb-6 pr-8">
              {editAddress ? 'Edit Address' : 'Add New Address'}
            </h2>
            <form className="grid grid-cols-1 gap-3 md:gap-4" onSubmit={formik.handleSubmit}>
              <div className="col-span-1">
                <label className="block font-medium mb-1 text-darktext text-sm md:text-base">Search for Address</label>
                <GooglePlacesAutocomplete
                  apiKey="AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4"
                  autocompletionRequest={{ componentRestrictions: { country: 'in' } }}
                  debounce={500}
                  selectProps={{
                    onChange: handlePlaceSelect,
                    placeholder: 'Enter your address...',
                    styles: {
                      input: (provided) => ({ ...provided, padding: '10px 10px 10px 0', width: '100%', textAlign: 'left', color: '#4B5563', borderRadius: '0.375rem', outline: 'none' }),
                      option: (provided) => ({ ...provided, color: '#333', textAlign: 'left' }),
                    },
                  }}
                />
              </div>
              <div className="col-span-1 flex justify-center mb-2 md:mb-4">
                <button type="button" onClick={handleCurrentLocation} className="flex items-center gap-2 bg-theme2 text-white px-3 py-2 md:px-4 md:py-2 rounded hover:bg-black text-sm md:text-base">
                  <FaLocationCrosshairs className="text-sm" /> Use Current Location
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">Address</label>
                  <input type="text" name="AddressLine1" placeholder="Enter Address" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.AddressLine1} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabledFields.AddressLine1} />
                  {formik.touched.AddressLine1 && formik.errors.AddressLine1 && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.AddressLine1}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">Landmark</label>
                  <input type="text" name="LandMark" placeholder="Enter Landmark" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.LandMark} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.LandMark && formik.errors.LandMark && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.LandMark}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">City</label>
                  <input type="text" name="City" placeholder="Enter City" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.City} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabledFields.City} />
                  {formik.touched.City && formik.errors.City && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.City}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">State</label>
                  <input type="text" name="State" placeholder="Enter State" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.State} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabledFields.State} />
                  {formik.touched.State && formik.errors.State && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.State}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">Country</label>
                  <select name="Country" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.Country} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabledFields.Country}>
                    <option value="">Select Country</option>
                    <option value="IN">India (IN)</option>
                    <option value="US">United States (US)</option>
                  </select>
                  {formik.touched.Country && formik.errors.Country && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.Country}</p>}
                </div>
                <div>
                  <label className="block font-medium mb-1 text-darktext text-sm md:text-base">Zip</label>
                  <input type="text" name="Pincode" placeholder="Enter your zip" className="w-full border rounded-md px-3 py-2 md:py-2.5 focus:outline-none text-sm md:text-base" value={formik.values.Pincode} onChange={formik.handleChange} onBlur={formik.handleBlur} disabled={disabledFields.Pincode} />
                  {formik.touched.Pincode && formik.errors.Pincode && <p className="text-red-500 text-xs md:text-sm mt-1">{formik.errors.Pincode}</p>}
                </div>
              </div>
              <div className="flex flex-row justify-end mt-4 md:mt-6 gap-2 md:gap-3">
                <button type="button" onClick={() => { setOpenModal(false); setEditAddress(null); formik.resetForm(); }} className="px-4 py-2 md:px-6 md:py-2 rounded-full text-gray-600 bg-gray-100 hover:bg-gray-200 text-sm md:text-base">Cancel</button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 md:px-6 md:py-2 bg-theme2 text-white rounded-full hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base">
                  {formLoading ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
